import { StrictOmit } from '@enmon/commons';
import { SerializedInventoryMeterReadingGroup } from '@enmon/commons/lib/tools';
import { useAppMessages } from 'hooks/useAppMessages';
import { useIntl } from 'react-intl';
import { useMutation, useQueryClient } from 'react-query';
import { useStore } from 'routes/inventorize_meter/useStore';
import { ReadingGroupsHooks as messages } from 'routes/reading_groups/messages';
import { CreateInventoryMeterReadingGroupData } from 'services/api/inventoryMeterReadingGroups';
import { useServices } from 'services/useServices';
import { MUTATION_KEYS, QUERY_KEYS } from 'utils/queryKeys';

export interface UseCreateInventoryMeterReadingGroupArgs {
  onSuccess?: (group: SerializedInventoryMeterReadingGroup) => void;
}

export type CreateInventoryMeterReadingGroupDataArg = StrictOmit<
  CreateInventoryMeterReadingGroupData,
  'inventory_location'
>;

export const useCreateInventoryMeterReadingGroup = ({ onSuccess }: UseCreateInventoryMeterReadingGroupArgs = {}) => {
  const intl = useIntl();
  const {
    api: { inventoryMeterReadingGroupsApiClient },
  } = useServices();
  const appMessages = useAppMessages();

  const location = useStore(state => state.location);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (createGroupData: CreateInventoryMeterReadingGroupDataArg) => {
      if (!location?.id) {
        throw new Error(intl.formatMessage(messages.locationError));
      }
      return inventoryMeterReadingGroupsApiClient.createOne({
        ...createGroupData,
        inventory_location: location.id,
      });
    },
    mutationKey: MUTATION_KEYS.inventoryMeterReadingGroups.create(),
    onSuccess: createdGroup => {
      appMessages.success(intl.formatMessage(messages.success));
      queryClient.invalidateQueries(QUERY_KEYS.inventoryMeterReadingGroups.all());
      onSuccess?.(createdGroup);
    },
  });
};
